import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon } from "../../utils/icons";
import Button from "../ui/Button";

const questions = [
  {
    id: 1,
    question: "What's your hair type?",
    options: [
      {
        value: "Type 3A-3C",
        label: "Type 3A-3C (Curly)",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743030996/3a-3c_kq8u1f.webp",
        description: "Defined curls with loose to tight spiral patterns",
      },
      {
        value: "Type 4A-4C",
        label: "Type 4A-4C (Coily)",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743031012/4a-4c_xckr6h.webp",
        description: "Tight coils with defined to undefined pattern",
      },
      {
        value: "Mixed Texture",
        label: "Mixed Texture",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743031194/mixed_textures_jfsy22.webp",
        description: "Multiple curl patterns and textures",
      },
    ],
  },
  {
    id: 2,
    question: "What's your main hair concern?",
    options: [
      {
        value: "Dryness",
        label: "Dryness",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743031148/dryness_iqsipn.webp",
        description: "Lack of moisture and shine",
      },
      {
        value: "Breakage",
        label: "Breakage",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743031106/breakage_vamaky.webp",
        description: "Split ends and fragile strands",
      },
      {
        value: "Growth",
        label: "Growth",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743031164/growth_yk0dw7.webp",
        description: "Length retention and thickness",
      },
      {
        value: "Maintenance",
        label: "Maintenance",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743031178/maintenance_kaa3kh.webp",
        description: "Daily styling and manageability",
      },
    ],
  },
  {
    id: 3,
    question: "How often do you wash your hair?",
    options: [
      {
        value: "Weekly",
        label: "Weekly",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743029784/babs_2_1_uuxjke.png",
        description: "Regular weekly washing routine",
      },
      {
        value: "Bi-weekly",
        label: "Bi-weekly",
        image:
          "https://images.unsplash.com/photo-1519699047748-de8e457a634e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        description: "Washing every two weeks",
      },
      {
        value: "Monthly",
        label: "Monthly",
        image:
          "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1743029515/babs1_3_txfjdu.jpg",
        description: "Monthly deep cleansing",
      },
    ],
  },
];

export default function HairQuiz({ onComplete }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showReview, setShowReview] = useState(false);

  const handleAnswer = (answer) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion]: answer }));
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      setShowReview(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleEditAnswer = (questionIndex) => {
    setShowReview(false);
    setCurrentQuestion(questionIndex);
  };

  const handleComplete = () => {
    onComplete(answers);
  };

  const getAnswerLabel = (questionIndex, answerValue) => {
    const question = questions[questionIndex];
    const option = question.options.find((opt) => opt.value === answerValue);
    return option ? option.label : "";
  };

  return (
    <div className="max-w-4xl mx-auto">
      <AnimatePresence mode="wait">
        {!showReview ? (
          <motion.div
            key="question"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl font-serif font-bold text-primary-900 mb-4">
                {questions[currentQuestion].question}
              </h3>
              <div className="w-full h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-[#FBB6B4] rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      ((currentQuestion + 1) / questions.length) * 100
                    }%`,
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {questions[currentQuestion].options.map((option) => (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAnswer(option.value)}
                  className={`group relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300 ${
                    answers[currentQuestion] === option.value
                      ? "ring-2 ring-[#FBB6B4]"
                      : ""
                  }`}
                >
                  <div className="aspect-[4/3] relative">
                    <img
                      src={option.image}
                      alt={option.label}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <h4 className="text-xl font-semibold text-white mb-2">
                        {option.label}
                      </h4>
                      <p className="text-sm text-gray-200">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            <div className="flex justify-between pt-6">
              <Button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ChevronLeftIcon className="w-5 h-5" />
                Previous
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="review"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8"
          >
            <h3 className="text-2xl font-serif font-bold text-primary-900 mb-6 text-center">
              Review Your Answers
            </h3>

            <div className="space-y-6">
              {questions.map((question, index) => (
                <div
                  key={question.id}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-lg font-semibold text-primary-900 mb-2">
                        {question.question}
                      </h4>
                      <p className="text-[#FBB6B4] font-medium">
                        {getAnswerLabel(index, answers[index])}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditAnswer(index)}
                      className="text-sm"
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center pt-6">
              <Button
                onClick={handleComplete}
                className="bg-[#FBB6B4] hover:bg-[#FBB6B4]/90 text-white px-8"
              >
                Get My Recommendations
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
